import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { authEvents } from '@/api/auth-events';
import { organizationScope } from '@/api/organization-scope';
import { viewingScope, type ViewedOrganization } from '@/api/viewing-scope';

import { ROLES } from './roles';
import { useAuth } from './useAuth';
import { ViewingContext, type ViewingContextValue } from './viewing-context';

/**
 * Staff looking at one organization's portal, read-only.
 *
 * Beside the signed-in person rather than part of them: the person has not
 * changed, only where they are looking. It sets two things and clears both:
 *
 * - `viewingScope`, which the API layer reads to refuse every write;
 * - `organizationScope`, which the fetch wrapper already turns into the
 *   `X-Organization-Id` header, so every page fetches the viewed
 *   organization's data without knowing it is being viewed.
 *
 * Only staff can be in a view. A Super Admin's own organization scope is set by
 * AuthProvider and never touched here, and a view value left in storage buys
 * an organization user nothing: it is cleared on sight.
 */
export function ViewingAsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isStaff = user?.role === ROLES.ACTIVIST_STAFF;

  const [viewing, setViewing] = useState<ViewedOrganization | null>(() =>
    viewingScope.get(),
  );
  const [arriving, setArriving] = useState(false);

  /*
   * The organization pages cache under keys that name no organization, so a
   * page opened in one view would show for a moment in the next. Dropping them
   * on every entry and exit is cheap and keeps each view's data its own.
   */
  const forgetOrganizationData = useCallback(() => {
    queryClient.removeQueries({ queryKey: ['account'] });
    queryClient.removeQueries({ queryKey: ['activity'] });
    queryClient.removeQueries({ queryKey: ['dashboard'] });
  }, [queryClient]);

  const clear = useCallback(() => {
    viewingScope.clear();
    setViewing(null);
    setArriving(false);
  }, []);

  /*
   * On a reload the stored view comes back, but AuthProvider writes the scope
   * only at sign-in, so it is put back here. Anybody who is not staff, or
   * nobody at all, has any stored view removed instead.
   */
  useEffect(() => {
    if (!viewing) return;
    if (!user) return;
    if (!isStaff) {
      // Only the stored copy needs removing: what this provider shares is
      // already null for anybody who is not staff, and the stored copy is what
      // the API layer would otherwise read and refuse writes by.
      viewingScope.clear();
      return;
    }
    organizationScope.set(viewing.id);
  }, [viewing, user, isStaff]);

  // Signing out ends the view with the session, whichever way it ended.
  useEffect(() => authEvents.onSignedOut(clear), [clear]);

  const enter = useCallback(
    (organization: ViewedOrganization) => {
      if (!isStaff) return;
      forgetOrganizationData();
      viewingScope.set(organization);
      organizationScope.set(organization.id);
      setViewing(organization);
      // The staff page View portal was pressed on is still on screen until
      // the router moves on, and must not end the view it just started.
      setArriving(true);
    },
    [isStaff, forgetOrganizationData],
  );

  const exit = useCallback(() => {
    forgetOrganizationData();
    viewingScope.clear();
    // Staff have no organization of their own, so there is nothing to go back
    // to: the scope is simply cleared.
    organizationScope.clear();
    setViewing(null);
    setArriving(false);
  }, [forgetOrganizationData]);

  const arrived = useCallback(() => setArriving(false), []);

  const value = useMemo<ViewingContextValue>(
    () => ({ viewing: isStaff ? viewing : null, enter, exit, arriving, arrived }),
    [isStaff, viewing, enter, exit, arriving, arrived],
  );

  return <ViewingContext.Provider value={value}>{children}</ViewingContext.Provider>;
}
